import React from "react";
import { Button } from "@/components";
import { Plus, Edit, Trash2, Check, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

export interface EntityOption {
  id: number;
  label: string;
  imageUrl?: string;
}

interface EntitySelectorProps {
  options: EntityOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  label: string;
  onAddNew: () => void;
  onView?: (id: number) => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  isLoading?: boolean;
}

export const EntitySelector: React.FC<EntitySelectorProps> = ({
  options,
  selectedIds,
  onChange,
  label,
  onAddNew,
  onView,
  onEdit,
  onDelete,
  isLoading = false,
}) => {
  const handleToggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((i) => i !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.id));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
        <label className="text-sm font-medium">{label}</label>
        <div className="flex gap-2">
          {options.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedIds.length === options.length ? "Deselect all" : "Select all"}
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={onAddNew}>
            <Plus className="h-4 w-4 mr-2" />
            Add new
          </Button>
        </div>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : options.length === 0 ? (
        <div className="border border-dashed border-input rounded-lg p-8 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            No {label.toLowerCase()} available. Create one to get started.
          </p>
          <Button onClick={onAddNew}>
            <Plus className="h-4 w-4 mr-2" />
            Create {label.slice(0, -1)}
          </Button>
        </div>
      ) : (
        <div className="border border-input rounded-lg divide-y divide-input max-h-64 overflow-y-auto">
          {options.map((option) => (
            <div
              key={option.id}
              role="button"
              tabIndex={0}
              onClick={() => handleToggle(option.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleToggle(option.id);
                }
              }}
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 group cursor-pointer"
            >
              <div
                className={cn(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-lg border-2 transition-all",
                  selectedIds.includes(option.id)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-primary/50 bg-background"
                )}
                aria-hidden
              >
                {selectedIds.includes(option.id) && (
                  <Check className="h-4 w-4" strokeWidth={2.5} />
                )}
              </div>
              {option.imageUrl && (
                <div className="w-10 h-10 shrink-0 rounded overflow-hidden bg-muted">
                  <img
                    src={option.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <span className="text-sm truncate flex-1 min-w-0">{option.label}</span>
              {(onView || onEdit || onDelete) && (
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  {onView && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onView(option.id);
                      }}
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  {onEdit && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEdit(option.id);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (window.confirm(`Delete "${option.label}"?`)) {
                          onDelete(option.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
